"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HostRoomSession = void 0;
const api_1 = require("@streem/api");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_error_1 = require("../../streem_error");
const build_room_invitation_1 = require("../../util/build_room_invitation");
const create_room_status_observable_1 = require("../../util/create_room_status_observable");
const logger_1 = require("../../util/logger");
const validate_config_shape_1 = require("../../util/validate_config_shape");
const ar_controller_1 = require("../controllers/ar_controller");
const room_session_1 = require("./room_session");
class HostRoomSession extends room_session_1.RoomSession {
    static initialize(config) {
        const _super = Object.create(null, {
            buildWallSyncClient: { get: () => super.buildWallSyncClient }
        });
        return __awaiter(this, void 0, void 0, function* () {
            return new HostRoomSession(config, yield _super.buildWallSyncClient.call(this, config));
        });
    }
    constructor(config, wallSyncClient) {
        super(config, wallSyncClient);
        this.config = config;
        /*
         *  This property was created as a response to a Twilio-Video defect that was discovered while testing the
         *  public iOS 18 bete release. Details on this defect can be found here: https://streempro.atlassian.net/browse/ST-6327
         *  A issue has been filed with Twilio-Video by the eta for a fix is unknown. This function and its associated code in
         *  the CallLobby, HostCallFrame, and DeviceTroubleShootingContent components should be removed once
         *  the defect has been resolved by Twilio-Video or Streem has fully migrated to Chime as a video platform.
         */
        this.isUsingTwilioAndIOS18 = false;
        (0, validate_config_shape_1.validateConfigShape)(['onEnd', 'localUserId', 'localDeviceId', 'remoteUserId', 'roomId'], config);
        try {
            this.ar = new ar_controller_1.ARController(Object.assign(Object.assign({}, this.controllerConfig), { mediaController: this.media }));
        }
        catch (error) {
            throw new streem_error_1.StreemError(`Failed to create ARController for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.roomInvitation = this.invitationController.outgoingInvitation.pipe((0, operators_1.switchMap)((roomInvitation) => __awaiter(this, void 0, void 0, function* () {
                const { fromUserSid } = roomInvitation;
                (0, toolbox_1.invariant)(fromUserSid, 'Room invitation missing sender fromUserSid');
                try {
                    const { user } = yield api_1.StreemAPI.users.getUser(fromUserSid);
                    return [roomInvitation, user];
                }
                catch (error) {
                    logger_1.logger.error(`Attempted to find user ${fromUserSid} and failed.`, error);
                    throw new streem_error_1.StreemError('Cannot find user for roomInvitation');
                }
            })), (0, operators_1.map)(([roomInvitation, user]) => (0, build_room_invitation_1.buildRoomInviteData)(roomInvitation, user)));
        }
        catch (error) {
            throw new streem_error_1.StreemError(`Failed to build RoomInvitation for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.roomInvitationStatus = this.invitationController.outgoingInvitationStatus.pipe((0, operators_1.map)(roomInvitationStatus => roomInvitationStatus
                ? build_room_invitation_1.protobufMapper.roomInvitationStatusFromV1(streem_sdk_protobuf_1.streem.api.RoomInvitation.Status[roomInvitationStatus])
                : 'UNKNOWN'));
        }
        catch (error) {
            throw new streem_error_1.StreemError(`Failed to update status of RoomInvitation for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        try {
            this.status = (0, create_room_status_observable_1.createRoomStatusObservable)(this.room.roomStatus, this.room.localParticipantStatus, this.room.remoteParticipantStatus, this.invitationController.outgoingInvitationStatus);
        }
        catch (error) {
            throw new streem_error_1.StreemError(`Failed to create Observable<RoomSessionStatus> for roomId=${this.roomId} localUserId=${this.config.localUserId} deviceId=${this.config.localDeviceId} error=${error}`);
        }
        this.isUsingTwilioAndIOS18 = (0, toolbox_1.checkIfHostIsUsingTwilioAndIOS18)(this.callSettings.videoPlatform);
    }
    // Asks for device permissions using browser's navigator API during audio & video track setup;
    createHostMediaTracks() {
        return __awaiter(this, void 0, void 0, function* () {
            // connect to the room wall
            yield this.connect();
            // create our tracks
            yield this.media.createHostTracks();
            // only record host video once the call has begun
            const roomStatusSub = (0, create_room_status_observable_1.onRoomStatus)({
                status: 'CONNECTED',
                source: this.status,
            })
                .pipe((0, operators_1.exhaustMap)(() => (0, rxjs_1.from)(this.startRecordingHostVideo())))
                .subscribe(() => {
                logger_1.logger.info(`room is connected. started recording host video`);
            }, err => {
                logger_1.logger.error(`failed to start recording host video: ${err}`);
            });
            this.subscriptionManager.add(roomStatusSub);
        });
    }
    startRecordingHostVideo() {
        return __awaiter(this, void 0, void 0, function* () {
            // connect to track provider
            yield this.media.connect(this.config.roomId, this.config.localUserId);
        });
    }
    // Call this method once you're ready to join the room.
    sendInvitation() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.debug('Host sending invitation to Spectator');
            return yield this.invitationController.createInvitation({
                fromUserId: this.config.localUserId,
                fromDeviceId: this.config.localDeviceId,
                toUserId: this.config.remoteUserId,
            });
        });
    }
    // TODO: wire this up to 'end call' button prior to pro accepting invitation
    cancelInvitation(invitationId) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info('Host cancelling invitation');
            yield this.invitationController.cancelInvitation(invitationId);
            yield this.media.disconnect();
            yield this.endSession();
        });
    }
}
exports.HostRoomSession = HostRoomSession;
HostRoomSession.canSendCameraSubjects = [
    streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD,
    streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON,
];
//# sourceMappingURL=host_room_session.js.map